import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSpaceSkill, useDeleteSpaceSkill } from '../../hooks/useSkills';
import { useEntityPermissions } from '../../hooks/useEntityPermissions';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, Card, CardHeader, ConfirmModal, ControlledTabs, MarkdownContent, getStatusVariant } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import PluginManagedBanner from '../../components/plugins/PluginManagedBanner';
import BackLink from '../../components/navigation/BackLink';
import { AuiFiles } from '@/components/aui/components/AuiFiles';

export default function SkillDetail() {
  const { skillId } = useParams<{ skillId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const { data: skill, isLoading, error } = useSpaceSkill(skillId);
  const deleteSkill = useDeleteSpaceSkill();
  const { canDelete } = useEntityPermissions();

  const [deleteModal, setDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !skill) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load skill</p>
        <Link to={routes.console.spaceSkills(spaceId)} className="text-primary-600 hover:text-primary-500 mt-2 inline-block">
          Back to skills
        </Link>
      </div>
    );
  }

  const handleDelete = async () => {
    try {
      await deleteSkill.mutateAsync(skill.skillId);
      success('Skill deleted successfully');
      navigate(routes.console.spaceSkills(spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete skill');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <BackLink to={routes.console.spaceSkills(spaceId)}>Skills</BackLink>
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{skill.name}</h1>
            <Badge variant={getStatusVariant(skill.status)}>{skill.status}</Badge>
            {skill.provenance?.source === 'plugin' && (
              <Badge variant="info">Plugin</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{skill.description}</p>
        </div>
        <Link to={routes.console.spaceSkillEdit(spaceId, skill.skillId)}>
          <Button>Edit Skill</Button>
        </Link>
      </div>
      </div>

      {skill.provenance?.source === 'plugin' && (
        <PluginManagedBanner provenance={skill.provenance} />
      )}

      <ControlledTabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'files', label: 'Files' },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      >
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Instructions */}
            <Card className="min-w-0">
              <CardHeader
                title="Instructions"
                description="Markdown content loaded when this skill is activated"
              />
              {skill.instructions ? (
                <div className="bg-muted/60 px-6 py-4 -mx-6 -mb-6 rounded-b-lg overflow-x-auto">
                  <MarkdownContent>{skill.instructions}</MarkdownContent>
                </div>
              ) : (
                <p className="text-muted-foreground">No instructions configured.</p>
              )}
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader title="Metadata" />
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Skill ID</dt>
                  <dd className="mt-1 text-sm text-foreground font-mono">
                    {skill.skillId}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                  <dd className="mt-1">
                    <Badge variant={getStatusVariant(skill.status)}>{skill.status}</Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {new Date(skill.createdAt).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {new Date(skill.updatedAt).toLocaleString()}
                  </dd>
                </div>
              </dl>
            </Card>

            {/* Danger zone — Admin+, or the creator of this skill */}
            {canDelete(skill) && (
              <Card className="border-red-200 dark:border-red-800">
                <CardHeader
                  title="Danger Zone"
                  description="Irreversible and destructive actions"
                />
                <Button variant="danger" onClick={() => setDeleteModal(true)}>
                  Delete Skill
                </Button>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          // Fixed viewport-relative height so AuiFiles (`h-full` inside)
          // renders at a stable size — selecting/swapping files won't resize
          // the page. Plugin-managed skills are read-only (managed externally).
          <div className="h-[calc(100vh-22rem)] min-h-[420px]">
            <AuiFiles
              key={skill.skillId}
              ownerId={skill.skillId}
              title="Skill Files"
              allowUpload={skill.provenance?.source !== 'plugin'}
              allowDelete={skill.provenance?.source !== 'plugin'}
            />
          </div>
        )}
      </ControlledTabs>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Skill"
        message={`Are you sure you want to delete "${skill.name}"? Agents using this skill will no longer have access to it. This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteSkill.isPending}
      />
    </div>
  );
}
